import React, { useState } from 'react';
import {
  TrendingUp,
  Users,
  Wallet,
  Gift,
  AlertTriangle,
  ChevronRight,
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Download,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AdminSidebar, AdminHeader } from '@/components/layout/Navigation';
import { KPICard, StatsMiniCard } from '@/components/cards/LoyaltyCards';
import { EarnRedeemChart, RedemptionPieChart, AdminBarChart } from '@/components/charts/LoyaltyCharts';
import {
  adminKPIs,
  adminChartData,
  topPartners,
  pointsRules,
  partnersAdmin,
  campaignsAdmin,
  fraudFlags,
  liveRedemptions,
  internalRedemptions,
} from '@/data/mockData';
import { useApp } from '@/context/AppContext';
import { cn } from '@/lib/utils';

// Admin Dashboard
export function AdminDashboard() {
  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Total Points Issued"
          value={adminKPIs.totalPointsIssued.toLocaleString()}
          change={adminKPIs.monthOverMonth.issued}
          changeType="positive"
          icon={TrendingUp}
          variant="primary"
        />
        <KPICard
          title="Total Redeemed"
          value={adminKPIs.totalPointsRedeemed.toLocaleString()}
          change={adminKPIs.monthOverMonth.redeemed}
          changeType="positive"
          icon={Gift}
        />
        <KPICard
          title="Outstanding Liability"
          value={`RM ${(adminKPIs.outstandingLiability / 100).toLocaleString()}`}
          icon={Wallet}
        />
        <KPICard
          title="Active Users"
          value={adminKPIs.activeUsers.toLocaleString()}
          change={adminKPIs.monthOverMonth.users}
          changeType="positive"
          icon={Users}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <EarnRedeemChart data={adminChartData.earnVsRedeem} height={280} />
        <RedemptionPieChart data={adminChartData.redemptionByCategory} height={280} />
      </div>

      {/* Bottom Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Partners */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              Top Partners by Redemptions
              <Button variant="ghost" size="sm">
                View All <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {topPartners.slice(0, 5).map((partner, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center text-sm font-semibold">
                      {index + 1}
                    </div>
                    <span className="font-medium">{partner.name}</span>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">{partner.redemptions.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">redemptions</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Quick Stats</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <StatsMiniCard label="Redemption Rate" value={`${adminKPIs.redemptionRate}%`} icon={TrendingUp} />
              <StatsMiniCard label="Avg Points/User" value={adminKPIs.avgPointsPerUser.toString()} icon={Users} />
              <StatsMiniCard label="Active Campaigns" value="3" icon={Gift} />
              <StatsMiniCard label="Pending Reviews" value="2" icon={AlertTriangle} />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Points Rules Engine
export function AdminPointsRules() {
  const { addNotification } = useApp();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-display font-semibold">Points Rules Engine</h2>
          <p className="text-muted-foreground">Configure earning rules for different services</p>
        </div>
        <Button onClick={() => addNotification({ type: 'info', message: 'Rule builder coming soon' })}>
          <Plus className="h-4 w-4 mr-2" />
          Create Rule
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Service Type</TableHead>
                <TableHead>Earn Rate</TableHead>
                <TableHead>Cap</TableHead>
                <TableHead>Streak Bonus</TableHead>
                <TableHead>Effective Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pointsRules.map((rule) => (
                <TableRow key={rule.id}>
                  <TableCell className="font-medium">{rule.serviceType}</TableCell>
                  <TableCell>
                    <Badge variant="points">+{rule.earnRate} pts</Badge>
                  </TableCell>
                  <TableCell>{rule.cap ? `${rule.cap} pts` : 'No cap'}</TableCell>
                  <TableCell>
                    {rule.streakBonus ? (
                      <Badge variant="success">Enabled</Badge>
                    ) : (
                      <Badge variant="secondary">Disabled</Badge>
                    )}
                  </TableCell>
                  <TableCell>{rule.effectiveDate}</TableCell>
                  <TableCell>
                    <Badge variant={rule.status === 'active' ? 'active' : 'secondary'}>
                      {rule.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem>
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem className="text-destructive">
                          <Trash2 className="h-4 w-4 mr-2" />
                          Disable
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

// Redemption Catalog
export function AdminCatalog() {
  const { addNotification } = useApp();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-display font-semibold">Redemption Catalog</h2>
          <p className="text-muted-foreground">Manage internal Physiomobile redemption items</p>
        </div>
        <Button onClick={() => addNotification({ type: 'info', message: 'Add item coming soon' })}>
          <Plus className="h-4 w-4 mr-2" />
          Add Item
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {internalRedemptions.map((item) => (
          <Card key={item.id} className={cn(!item.available && 'opacity-60')}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-semibold">{item.title}</h3>
                <Badge variant={item.available ? 'active' : 'secondary'}>
                  {item.available ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{item.description}</p>
              <div className="flex items-center justify-between text-sm">
                <Badge variant="points">{item.pointsCost.toLocaleString()} pts</Badge>
                <span className="text-muted-foreground">{item.savingsValue}</span>
              </div>
              <div className="flex items-center gap-2 mt-4">
                <Button variant="outline" size="sm" className="flex-1">
                  <Edit className="h-3 w-3 mr-1" />
                  Edit
                </Button>
                <Button variant="ghost" size="sm">
                  <Eye className="h-3 w-3" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// Campaigns Manager
export function AdminCampaigns() {
  const { addNotification } = useApp();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-display font-semibold">Campaigns & Challenges</h2>
          <p className="text-muted-foreground">Manage loyalty campaigns and challenges</p>
        </div>
        <Button onClick={() => addNotification({ type: 'info', message: 'Campaign wizard coming soon' })}>
          <Plus className="h-4 w-4 mr-2" />
          Create Campaign
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Campaign Name</TableHead>
                <TableHead>Period</TableHead>
                <TableHead>Target Segment</TableHead>
                <TableHead>Budget Used</TableHead>
                <TableHead>Participants</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaignsAdmin.map((campaign) => (
                <TableRow key={campaign.id}>
                  <TableCell className="font-medium">{campaign.name}</TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">
                      {campaign.startDate} - {campaign.endDate}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{campaign.targetSegment}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="text-sm">
                        {campaign.budgetUsed.toLocaleString()} / {campaign.budgetCap.toLocaleString()}
                      </div>
                      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full"
                          style={{ width: `${(campaign.budgetUsed / campaign.budgetCap) * 100}%` }}
                        />
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{campaign.participants}</TableCell>
                  <TableCell>
                    <Badge variant={campaign.status === 'active' ? 'active' : 'secondary'}>
                      {campaign.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm">
                      View Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

// Partners Management
export function AdminPartners() {
  const { addNotification } = useApp();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-display font-semibold">Partner Management</h2>
          <p className="text-muted-foreground">Manage partner merchants and their offers</p>
        </div>
        <Button onClick={() => addNotification({ type: 'info', message: 'Partner onboarding coming soon' })}>
          <Plus className="h-4 w-4 mr-2" />
          Add Partner
        </Button>
      </div>

      {/* Search and Filter */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search partners..." className="pl-9" />
        </div>
        <Button variant="outline">
          <Filter className="h-4 w-4 mr-2" />
          Filter
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Partner Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Branches</TableHead>
                <TableHead>Total Redemptions</TableHead>
                <TableHead>Pending Settlement</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {partnersAdmin.map((partner) => (
                <TableRow key={partner.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                        <span className="text-sm font-semibold">{partner.name.charAt(0)}</span>
                      </div>
                      <div>
                        <p className="font-medium">{partner.name}</p>
                        <p className="text-xs text-muted-foreground">{partner.contactEmail}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{partner.category}</Badge>
                  </TableCell>
                  <TableCell>{partner.branches}</TableCell>
                  <TableCell>{partner.totalRedemptions.toLocaleString()}</TableCell>
                  <TableCell>RM {partner.pendingSettlement.toLocaleString()}</TableCell>
                  <TableCell>
                    <Badge
                      variant={partner.status === 'active' ? 'active' : partner.status === 'pending' ? 'pending' : 'secondary'}
                    >
                      {partner.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem>
                          <Eye className="h-4 w-4 mr-2" />
                          View Profile
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        {partner.status === 'pending' && (
                          <DropdownMenuItem>
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Approve
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

// Vouchers Monitor
export function AdminVoucherMonitor() {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-display font-semibold">Vouchers & Redemptions</h2>
          <p className="text-muted-foreground">Monitor live redemptions and voucher activity</p>
        </div>
        <Button variant="outline">
          <Download className="h-4 w-4 mr-2" />
          Export
        </Button>
      </div>

      {/* Live Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatsMiniCard label="Today's Redemptions" value="156" icon={Gift} />
        <StatsMiniCard label="Issued" value="89" icon={CheckCircle} />
        <StatsMiniCard label="Redeemed" value="67" icon={Clock} />
        <StatsMiniCard label="Expired Today" value="12" icon={XCircle} />
      </div>

      {/* Live Redemptions Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Live Redemptions</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Partner</TableHead>
                <TableHead>Voucher Code</TableHead>
                <TableHead>Points</TableHead>
                <TableHead>Time</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {liveRedemptions.map((redemption) => (
                <TableRow key={redemption.id}>
                  <TableCell className="font-medium">{redemption.userName}</TableCell>
                  <TableCell>{redemption.partner}</TableCell>
                  <TableCell>
                    <code className="text-xs bg-muted px-2 py-1 rounded">{redemption.voucherCode}</code>
                  </TableCell>
                  <TableCell>
                    <Badge variant="destructive">-{redemption.pointsDeducted.toLocaleString()}</Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">
                      {new Date(redemption.timestamp).toLocaleTimeString()}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant={redemption.status === 'redeemed' ? 'success' : 'warning'}>
                      {redemption.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm">
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

// Fraud Review
export function AdminFraudReview() {
  const { addNotification } = useApp();

  const handleAction = (action, flag) => {
    addNotification({ type: 'success', message: `${action} action taken on ${flag.userName}` });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-display font-semibold">Fraud & Abuse Review</h2>
        <p className="text-muted-foreground">Review flagged accounts and suspicious activity</p>
      </div>

      {/* Pending Reviews */}
      <div className="space-y-4">
        {fraudFlags.map((flag) => (
          <Card key={flag.id} className={cn(flag.status === 'resolved' && 'opacity-60')}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div
                    className={cn(
                      'w-10 h-10 rounded-full flex items-center justify-center',
                      flag.riskLevel === 'high' && 'bg-destructive/10 text-destructive',
                      flag.riskLevel === 'medium' && 'bg-warning/10 text-warning',
                      flag.riskLevel === 'low' && 'bg-muted text-muted-foreground'
                    )}
                  >
                    <AlertTriangle className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold">{flag.userName}</h3>
                      <Badge
                        variant={flag.riskLevel === 'high' ? 'destructive' : flag.riskLevel === 'medium' ? 'warning' : 'secondary'}
                      >
                        {flag.riskLevel} risk
                      </Badge>
                      {flag.status === 'resolved' && <Badge variant="success">Resolved</Badge>}
                    </div>
                    <p className="text-sm font-medium text-foreground">{flag.flagReason}</p>
                    <p className="text-sm text-muted-foreground mt-1">{flag.details}</p>
                    <p className="text-xs text-muted-foreground mt-2">Flagged on {flag.date}</p>
                    {flag.resolution && (
                      <p className="text-sm text-success mt-2">Resolution: {flag.resolution}</p>
                    )}
                  </div>
                </div>
                {flag.status !== 'resolved' && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleAction('Approve', flag)}
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleAction('Suspend', flag)}
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Suspend
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// Reports
export function AdminReports() {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-display font-semibold">Finance & Audit Reports</h2>
          <p className="text-muted-foreground">Generate and export financial reports</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[
          { title: 'Liability Report', description: 'Outstanding points liability breakdown', icon: Wallet },
          { title: 'Points Aging Report', description: 'Points expiry analysis by period', icon: Clock },
          { title: 'Partner Settlement', description: 'Settlement summary by partner', icon: Users },
          { title: 'Monthly Summary', description: 'Monthly earn/redeem summary', icon: TrendingUp },
          { title: 'User Activity Report', description: 'User engagement metrics', icon: Users },
          { title: 'Campaign ROI Report', description: 'Campaign performance analysis', icon: Gift },
        ].map((report, index) => (
          <Card key={index} className="card-interactive cursor-pointer">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <report.icon className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold mb-1">{report.title}</h3>
                  <p className="text-sm text-muted-foreground">{report.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-4">
                <Button variant="outline" size="sm" className="flex-1">
                  <Download className="h-3 w-3 mr-1" />
                  CSV
                </Button>
                <Button variant="outline" size="sm" className="flex-1">
                  <Download className="h-3 w-3 mr-1" />
                  PDF
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Chart */}
      <AdminBarChart data={adminChartData.earnVsRedeem} title="Monthly Earn vs Redeem Trend" height={300} />
    </div>
  );
}

// Admin Layout Wrapper
export function AdminLayout() {
  const { activeTab } = useApp();

  const getTitle = () => {
    const titles = {
      dashboard: 'Loyalty Dashboard',
      rules: 'Points Rules Engine',
      catalog: 'Redemption Catalog',
      campaigns: 'Campaigns & Challenges',
      partners: 'Partner Management',
      vouchers: 'Vouchers Monitor',
      fraud: 'Fraud Review',
      reports: 'Reports & Finance',
    };
    return titles[activeTab] || 'Dashboard';
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <AdminDashboard />;
      case 'rules':
        return <AdminPointsRules />;
      case 'catalog':
        return <AdminCatalog />;
      case 'campaigns':
        return <AdminCampaigns />;
      case 'partners':
        return <AdminPartners />;
      case 'vouchers':
        return <AdminVoucherMonitor />;
      case 'fraud':
        return <AdminFraudReview />;
      case 'reports':
        return <AdminReports />;
      default:
        return <AdminDashboard />;
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebar />
      <div className="flex-1 flex flex-col">
        <AdminHeader title={getTitle()} />
        <main className="flex-1 p-6 overflow-auto">{renderContent()}</main>
      </div>
    </div>
  );
}
